import { baseApi } from "./baseApi";
import type {
  TicketData,
  TicketAttachment,
} from "../../pages/Crm/components/HelpDesk/utils/types";
import type { HelpdeskViewItem } from "../../pages/Crm/components/HelpDesk/components/AllTickets/utils/types";
import { HELPDESK_VIEWS_PAGE_SIZE } from "../../pages/Crm/components/HelpDesk/components/AllTickets/utils/constant";

export interface GetHelpdeskViewsArg {
  page?: number;
  limit?: number;
}

export interface GetStageTicketsParams {
  stage_key: string;
  limit?: number;
  page?: number;
  cursor?: string;
  assignees?: string[];
  priority?: string[];
  tags?: string[];
  search?: string;
  ticket_category?: string;
  property_name?: string;
  sort_key?: "created_at" | "updated_at" | "ticket_title";
  sort_order?: "asc" | "desc";
  view_id?: string;
  system_name?: string;
  is_unassigned?: boolean;
}

export interface FilterCondition {
  key: string;
  comparison_operator: string;
  value: string | string[] | number | number[];
  is_custom_field?: boolean;
}

export interface FilterGroup {
  conditions: FilterCondition[];
  logical_operator: "and" | "or";
}

export interface CreateHelpdeskViewParams {
  name: string;
  scope: "private" | "public";
  filter_groups: FilterGroup[];
  logical_operator: "and" | "or";
  emoji_icon: string;
}

export interface UpdateHelpdeskViewPayload extends Partial<CreateHelpdeskViewParams> {
  viewId: string;
}

export interface CreateHelpdeskTicketPayload {
  stage_key: string;
  assignees: string[];
  ticket_category: string;
  name: string;
  email: string;
  property_name: string;
  title: string;
  priority?: string | null;
  description: string;
  attachments: Omit<TicketAttachment, "s3_url">[];
  tags: string[];
  request_support?: boolean;
  custom_fields?: Record<string, string | number | boolean | string[] | null>;
}

export interface UpdateTicketPayload {
  ticketId: string;
  title?: string;
  stage_key?: string;
  priority?: string | null;
  assignees?: string[];
  property_name?: string;
  due_date?: string | null;
  tags?: string[];
  description?: string;
  custom_fields?: Record<string, string | number | boolean | string[] | null>;
  attachments?: {
    s3_key: string;
    file_name: string;
    content_type: string;
    size_in_bytes: number;
    s3_url: string;
  }[];
  tagged_user_ids?: string[];
  resolution_reason?: string;
  external_vendor?: string;
  ticket_category?: string;
}

export interface CreateTicketCommentPayload {
  ticket_id: string;
  content: string;
  parent_comment_id?: string;
  attachments?: {
    s3_key: string;
    file_name: string;
    content_type: string;
    size_in_bytes: number;
    s3_url: string;
  }[];
  tagged_user_ids?: string[];
}

export interface UpdateTicketCommentPayload {
  comment_id: string;
  content: string;
  attachments?: {
    s3_key: string;
    file_name: string;
    content_type: string;
    size_in_bytes: number;
    s3_url: string;
  }[];
  tagged_user_ids?: string[];
}

interface TicketReportParams {
  client_code?: string;
  start_date?: string;
  end_date?: string;
}

/** Raw API response may include `custom_details` / `customDetails`; merge into `custom_fields`. */
function normalizeHelpdeskTicketGetResponse(data: TicketData): TicketData {
  const d = data as TicketData & {
    custom_details?: Record<string, string | number | boolean | string[]>;
    customDetails?: Record<string, string | number | boolean | string[]>;
  };
  const rawDetails = d.custom_details ?? d.customDetails;
  if (
    !rawDetails ||
    typeof rawDetails !== "object" ||
    Array.isArray(rawDetails)
  ) {
    return d;
  }
  d.custom_fields = { ...rawDetails, ...(d.custom_fields ?? {}) };
  return d;
}

export const helpdeskApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getHelpdeskTickets: builder.query({
      query: (payload) => ({
        url: `/helpdesk/board/query`,
        method: "POST",
        body: payload,
      }),
      providesTags: [{ type: "HelpdeskBoard", id: "LIST" }],
    }),
    getStageTickets: builder.query({
      query: ({
        stage_key,
        limit,
        page,
        cursor,
        assignees,
        priority,
        tags,
        search,
        ticket_category,
        property_name,
        sort_key,
        sort_order,
        view_id,
        system_name,
        is_unassigned,
      }: GetStageTicketsParams) => {
        const params = new URLSearchParams();
        params.set("stage_key", stage_key);
        if (limit) params.set("limit", String(limit));
        if (page) params.set("page", String(page));
        if (cursor) params.set("cursor", cursor);
        if (assignees?.length) {
          assignees.forEach((a) => params.append("assignees", a));
        }
        if (priority?.length) {
          priority.forEach((p) => params.append("priority", p));
        }
        if (tags?.length) {
          tags.forEach((t) => params.append("tags", t));
        }
        if (search) params.set("search", search);
        if (ticket_category) params.set("ticket_category", ticket_category);
        if (property_name) params.set("property_name", property_name);
        if (sort_key) params.set("sort_key", sort_key);
        if (sort_order) params.set("sort_order", sort_order);
        if (is_unassigned) params.set("is_unassigned", "true");
        if (view_id) params.set("view_id", view_id);
        if (system_name) params.set("system_name", system_name);
        return {
          url: `/helpdesk/tickets?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [{ type: "StageTickets", id: "LIST" }],
    }),
    getTicket: builder.query<TicketData, string>({
      query: (ticket_id) => ({
        url: `/helpdesk/tickets/${ticket_id}`,
        method: "GET",
      }),
      transformResponse: normalizeHelpdeskTicketGetResponse,
      providesTags: (_result, _error, ticket_id) => [
        { type: "HelpdeskTicket", id: ticket_id },
      ],
    }),
    getHelpdeskStages: builder.query({
      query: () => ({
        url: `/helpdesk/stages`,
        method: "GET",
      }),
    }),
    createHelpdeskTicket: builder.mutation({
      query: (body: CreateHelpdeskTicketPayload) => ({
        url: `/helpdesk/tickets`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "StageTickets", id: "LIST" },
        { type: "HelpdeskBoard", id: "LIST" },
        { type: "TicketsByStageReport", id: "LIST" },
      ],
    }),
    deleteTicket: builder.mutation({
      query: (ticket_id: string) => ({
        url: `/helpdesk/tickets/${ticket_id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "StageTickets", id: "LIST" },
        { type: "HelpdeskBoard", id: "LIST" },
        { type: "TicketsByStageReport", id: "LIST" },
      ],
    }),
    uploadKnowledgeBaseDocuments: builder.mutation({
      query: ({
        files,
        categories,
      }: {
        files: File[];
        categories: string[];
      }) => {
        const formData = new FormData();
        files.forEach((file) => {
          formData.append("files", file);
        });
        categories.forEach((category) => {
          formData.append("categories", category);
        });

        return {
          url: "/helpdesk/knowledge-base/documents/upload",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [
        { type: "HelpdeskDocuments", id: "LIST" },
        { type: "HelpdeskCategories", id: "LIST" },
      ],
    }),
    getKnowledgeBaseCategories: builder.query({
      query: () => ({
        url: "/helpdesk/knowledge-base/categories",
        method: "GET",
      }),
      providesTags: [{ type: "HelpdeskCategories", id: "LIST" }],
    }),
    uploadKnowledgeBaseCustomFAQs: builder.mutation({
      query: ({
        category,
        qa_pairs,
      }: {
        category: string;
        qa_pairs: Array<{ question: string; answer: string }>;
      }) => ({
        url: "/helpdesk/knowledge-base/custom-faqs",
        method: "POST",
        body: {
          category,
          qa_pairs,
        },
      }),
      invalidatesTags: [
        { type: "HelpdeskDocuments", id: "LIST" },
        { type: "HelpdeskCategories", id: "LIST" },
      ],
    }),
    getKnowledgeBaseDocuments: builder.query({
      query: ({
        skip = 0,
        limit = 10,
        sort_by = "updated_at",
        sort_order = "desc",
        search,
        categories,
      }: {
        skip?: number;
        limit?: number;
        sort_by?: string;
        sort_order?: string;
        search?: string;
        categories?: string[];
      } = {}) => {
        const params = new URLSearchParams();
        params.set("skip", skip.toString());
        params.set("limit", limit.toString());
        params.set("sort_by", sort_by);
        params.set("sort_order", sort_order);
        if (search) params.set("search", search);
        if (categories?.length) {
          categories.forEach((c) => params.append("categories", c));
        }

        return {
          url: `/helpdesk/knowledge-base/documents?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [{ type: "HelpdeskDocuments", id: "LIST" }],
    }),
    deleteKnowledgeBaseDocument: builder.mutation({
      query: (documentId: string) => ({
        url: `/helpdesk/knowledge-base/documents/${documentId}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "HelpdeskDocuments", id: "LIST" },
        { type: "HelpdeskCategories", id: "LIST" },
      ],
    }),
    getKnowledgeBaseDocumentPreviewUrl: builder.query({
      query: ({
        documentId,
        isPreview = false,
      }: {
        documentId: string;
        isPreview?: boolean;
      }) => {
        const params = new URLSearchParams();
        params.set("is_preview", isPreview.toString());

        return {
          url: `/helpdesk/knowledge-base/documents/${documentId}/access?${params.toString()}`,
          method: "GET",
        };
      },
    }),

    getTicketsByStageReport: builder.query({
      query: ({
        client_code,
        start_date,
        end_date,
      }: TicketReportParams = {}) => {
        const params = new URLSearchParams();
        if (client_code) params.set("client_code", client_code);
        if (start_date) params.set("start_date", start_date);
        if (end_date) params.set("end_date", end_date);

        return {
          url: `/helpdesk/reports/tickets-by-stage${params.toString() ? `?${params.toString()}` : ""}`,
          method: "GET",
        };
      },
      providesTags: [{ type: "TicketsByStageReport", id: "LIST" }],
    }),

    getTicketsByPriority: builder.query({
      query: ({
        client_code,
        start_date,
        end_date,
      }: TicketReportParams = {}) => {
        const params = new URLSearchParams();
        if (client_code) params.set("client_code", client_code);
        if (start_date) params.set("start_date", start_date);
        if (end_date) params.set("end_date", end_date);

        return {
          url: `/helpdesk/reports/tickets-by-priority${params.toString() ? `?${params.toString()}` : ""}`,
          method: "GET",
        };
      },
    }),
    getTicketsBySlaCompliance: builder.query({
      query: ({
        client_code,
        start_date,
        end_date,
      }: TicketReportParams = {}) => {
        const params = new URLSearchParams();
        if (client_code) params.set("client_code", client_code);
        if (start_date) params.set("start_date", start_date);
        if (end_date) params.set("end_date", end_date);

        return {
          url: `/helpdesk/reports/tickets-by-sla${params.toString() ? `?${params.toString()}` : ""}`,
          method: "GET",
        };
      },
    }),
    getHelpdeskViews: builder.query<
      { views?: HelpdeskViewItem[]; has_next?: boolean },
      GetHelpdeskViewsArg | void
    >({
      query: (arg) => {
        const page = arg?.page ?? 1;
        const limit = arg?.limit ?? HELPDESK_VIEWS_PAGE_SIZE;
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("limit", String(limit));
        return {
          url: `/helpdesk/views?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [{ type: "HelpdeskViews", id: "LIST" }],
      keepUnusedDataFor: 0,
    }),
    getHelpdeskView: builder.query<HelpdeskViewItem, string>({
      query: (view_id) => ({
        url: `/helpdesk/views/${view_id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, view_id) => [
        { type: "HelpdeskViews", id: view_id },
      ],
    }),
    createHelpdeskView: builder.mutation({
      query: (body: CreateHelpdeskViewParams) => ({
        url: "/helpdesk/views",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "HelpdeskViews", id: "LIST" }],
    }),
    updateHelpdeskView: builder.mutation<
      HelpdeskViewItem,
      UpdateHelpdeskViewPayload
    >({
      query: ({ viewId, ...body }) => ({
        url: `/helpdesk/views/${viewId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { viewId }) => [
        { type: "HelpdeskViews", id: "LIST" },
        { type: "HelpdeskBoard", id: "LIST" },
        { type: "HelpdeskViews", id: viewId },
      ],
    }),
    deleteHelpdeskView: builder.mutation({
      query: (viewId: string) => ({
        url: `/helpdesk/views/${viewId}`,
        method: "DELETE",
      }),
    }),
    getHelpdeskTags: builder.query({
      query: () => ({
        url: "/helpdesk/tags",
        method: "GET",
      }),
      providesTags: [{ type: "HelpdeskTags", id: "LIST" }],
    }),
    getHelpdeskResolutionReasons: builder.query({
      query: () => ({
        url: "/helpdesk/resolution-reasons",
        method: "GET",
      }),
      providesTags: [{ type: "HelpdeskResolutionReasons", id: "LIST" }],
    }),
    createHelpdeskTag: builder.mutation({
      query: (body: { title: string }) => ({
        url: "/helpdesk/tags",
        method: "POST",
        body,
      }),
    }),
    getHelpdeskCustomFields: builder.query({
      query: () => ({
        url: "/helpdesk/custom-fields",
        method: "GET",
      }),
      providesTags: [{ type: "HelpdeskCustomFields", id: "LIST" }],
    }),
    createHelpdeskCustomField: builder.mutation({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      query: (body: any) => ({
        url: "/helpdesk/custom-fields",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "HelpdeskCustomFields", id: "LIST" }],
    }),
    updateHelpdeskCustomField: builder.mutation({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      query: ({ key, ...body }: { key: string; [k: string]: any }) => ({
        url: `/helpdesk/custom-fields/${key}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [{ type: "HelpdeskCustomFields", id: "LIST" }],
    }),
    deleteHelpdeskCustomField: builder.mutation({
      query: (key: string) => ({
        url: `/helpdesk/custom-fields/${key}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "HelpdeskCustomFields", id: "LIST" }],
    }),
    updateTicket: builder.mutation({
      query: ({ ticketId, ...body }: UpdateTicketPayload) => ({
        url: `/helpdesk/tickets/${ticketId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { ticketId }) => [
        { type: "HelpdeskTicket", id: ticketId },
        { type: "StageTickets", id: "LIST" },
        { type: "HelpdeskBoard", id: "LIST" },
        { type: "TicketsByStageReport", id: "LIST" },
      ],
    }),
    getTicketsByCustomField: builder.query({
      query: ({
        key,
        client_code,
        start_date,
        end_date,
      }: {
        key: string;
      } & TicketReportParams) => {
        const params = new URLSearchParams();
        params.set("key", key);
        if (client_code) params.set("client_code", client_code);
        if (start_date) params.set("start_date", start_date);
        if (end_date) params.set("end_date", end_date);

        return {
          url: `/helpdesk/reports/tickets-by-custom-field${params.toString() ? `?${params.toString()}` : ""}`,
          method: "GET",
        };
      },
    }),
    getTicketsBySite: builder.query({
      query: ({
        client_code,
        start_date,
        end_date,
      }: TicketReportParams = {}) => {
        const params = new URLSearchParams();
        if (client_code) params.set("client_code", client_code);
        if (start_date) params.set("start_date", start_date);
        if (end_date) params.set("end_date", end_date);

        return {
          url: `/helpdesk/reports/tickets-by-site${params.toString() ? `?${params.toString()}` : ""}`,
          method: "GET",
        };
      },
    }),
    getTicketsByMonth: builder.query({
      query: ({
        client_code,
        start_date,
        end_date,
      }: TicketReportParams = {}) => {
        const params = new URLSearchParams();
        if (client_code) params.set("client_code", client_code);
        if (start_date) params.set("start_date", start_date);
        if (end_date) params.set("end_date", end_date);

        return {
          url: `/helpdesk/reports/tickets-by-month${params.toString() ? `?${params.toString()}` : ""}`,
          method: "GET",
        };
      },
    }),
    getTicketComments: builder.query({
      query: ({
        ticket_id,
        sort_order,
        search,
      }: {
        ticket_id: string;
        sort_order?: "asc" | "desc";
        search?: string;
      }) => {
        const params = new URLSearchParams();
        if (sort_order) params.set("sort_order", sort_order);
        if (search) params.set("search", search);
        const qs = params.toString();
        return {
          url: `/helpdesk/tickets/${ticket_id}/comments${qs ? `?${qs}` : ""}`,
          method: "GET",
        };
      },
      providesTags: [{ type: "TicketComments", id: "LIST" }],
    }),
    createTicketComment: builder.mutation({
      query: ({ ticket_id, ...body }: CreateTicketCommentPayload) => ({
        url: `/helpdesk/tickets/${ticket_id}/comments`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "TicketComments", id: "LIST" }],
    }),
    updateTicketComment: builder.mutation({
      query: ({ comment_id, ...body }: UpdateTicketCommentPayload) => ({
        url: `/helpdesk/comments/${comment_id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [{ type: "TicketComments", id: "LIST" }],
    }),
    deleteTicketComment: builder.mutation({
      query: (comment_id: string) => ({
        url: `/helpdesk/comments/${comment_id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "TicketComments", id: "LIST" }],
    }),
    searchHelpdeskTickets: builder.query<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      { search: string; page?: number; limit?: number }
    >({
      query: ({ search, page = 1, limit = 20 }) => {
        const params = new URLSearchParams();
        params.set("search", search);
        params.set("page", String(page));
        params.set("limit", String(limit));
        return {
          url: `/helpdesk/board/search?${params.toString()}`,
          method: "GET",
        };
      },
    }),
    downloadTicketAttachments: builder.mutation({
      query: ({ ticketId }: { ticketId: string }) => ({
        url: `/helpdesk/tickets/${ticketId}/attachments/download`,
        method: "GET",
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    getTicketAttachments: builder.query({
      query: (ticket_id: string) => ({
        url: `/helpdesk/tickets/${ticket_id}/attachments`,
        method: "GET",
      }),
      providesTags: [{ type: "TicketAttachments", id: "LIST" }],
    }),
    createTicketAttachment: builder.mutation({
      query: ({ ticket_id, ...body }) => ({
        url: `/helpdesk/tickets/${ticket_id}/attachments`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "TicketAttachments", id: "LIST" },
        { type: "HelpdeskTicket", id: "LIST" },
      ],
    }),
    deleteTicketAttachment: builder.mutation({
      query: ({
        ticket_id,
        s3_keys,
      }: {
        ticket_id: string;
        s3_keys: string[];
      }) => ({
        url: `/helpdesk/tickets/${ticket_id}/attachments`,
        method: "DELETE",
        body: { s3_keys },
      }),
      invalidatesTags: [
        { type: "TicketAttachments", id: "LIST" },
        { type: "HelpdeskTicket", id: "LIST" },
      ],
    }),
    uploadHelpdeskAttachment: builder.mutation({
      query: (payload: {
        attachment: File;
        source: string;
        bucket_type?: string;
      }) => {
        const formData = new FormData();
        formData.append("files", payload.attachment);
        formData.append("source", payload.source);
        if (payload.bucket_type) {
          formData.append("bucket_type", payload.bucket_type);
        }
        return {
          url: `/attachments/`,
          method: "POST",
          body: formData,
        };
      },
    }),

    getTicketCategories: builder.query<
      {
        key: string;
        display_name: string;
        created_at: string;
        updated_at: string;
      }[],
      void
    >({
      query: () => ({
        url: "/helpdesk/ticket-categories",
        method: "GET",
      }),
      providesTags: [{ type: "TicketCategories", id: "LIST" }],
    }),

    verifyClientUpdateIndependentOfCategory: builder.query<boolean, void>({
      query: () => ({
        url: "/helpdesk/ticket-categories/verify-client-update-based-independent-of-category",
        method: "GET",
      }),
    }),

    addSystemNameValues: builder.mutation<
      void,
      { values: string[] }
    >({
      query: (body) => ({
        url: "/helpdesk/custom-fields/system-name/values",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "HelpdeskCustomFields", id: "LIST" }],
    }),

    addIssueTypeValues: builder.mutation<
      void,
      { system_name: string; values: string[] }
    >({
      query: (body) => ({
        url: "/helpdesk/custom-fields/issue-type/values",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "HelpdeskCustomFields", id: "LIST" }],
    }),

    sendHelpdeskMail: builder.mutation({
      query: (payload: {
        ticket_id: string;
        from_email: string;
        to: string[];
        subject: string;
        cc: string[];
        bcc: string[];
        content: string;
        attachments: {
          s3_key: string;
          s3_url: string;
          file_name: string;
          content_type: string;
          size_in_bytes: number;
        }[];
        thread_id?: string;
        parent_mail_id?: string;
        unsubscribe_enabled?: boolean;
      }) => {
        return {
          url: `/helpdesk/tickets/${payload.ticket_id}/mail`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: [{ type: "TicketComments", id: "LIST" }],
    }),
  }),
});

export const {
  useGetHelpdeskTicketsQuery,
  useGetStageTicketsQuery,
  useGetTicketQuery,
  useGetHelpdeskStagesQuery,
  useCreateHelpdeskTicketMutation,
  useDeleteTicketMutation,
  useUploadKnowledgeBaseDocumentsMutation,
  useGetKnowledgeBaseCategoriesQuery,
  useUploadKnowledgeBaseCustomFAQsMutation,
  useGetKnowledgeBaseDocumentsQuery,
  useDeleteKnowledgeBaseDocumentMutation,
  useLazyGetKnowledgeBaseDocumentPreviewUrlQuery,
  useGetTicketsByStageReportQuery,
  useGetTicketsByPriorityQuery,
  useGetTicketsBySlaComplianceQuery,
  useGetTicketsByCustomFieldQuery,
  useGetTicketsBySiteQuery,
  useGetTicketsByMonthQuery,
  useGetHelpdeskViewsQuery,
  useGetHelpdeskViewQuery,
  useLazyGetHelpdeskViewQuery,
  useCreateHelpdeskViewMutation,
  useUpdateHelpdeskViewMutation,
  useDeleteHelpdeskViewMutation,
  useGetHelpdeskTagsQuery,
  useGetHelpdeskResolutionReasonsQuery,
  useCreateHelpdeskTagMutation,
  useGetHelpdeskCustomFieldsQuery,
  useCreateHelpdeskCustomFieldMutation,
  useUpdateHelpdeskCustomFieldMutation,
  useDeleteHelpdeskCustomFieldMutation,
  useUpdateTicketMutation,
  useGetTicketCommentsQuery,
  useCreateTicketCommentMutation,
  useUpdateTicketCommentMutation,
  useDeleteTicketCommentMutation,
  useDownloadTicketAttachmentsMutation,
  useUploadHelpdeskAttachmentMutation,
  useLazySearchHelpdeskTicketsQuery,
  useGetTicketAttachmentsQuery,
  useCreateTicketAttachmentMutation,
  useDeleteTicketAttachmentMutation,
  useSendHelpdeskMailMutation,
  useGetTicketCategoriesQuery,
  useVerifyClientUpdateIndependentOfCategoryQuery,
  useAddSystemNameValuesMutation,
  useAddIssueTypeValuesMutation,
} = helpdeskApi;
